import { tempoApi } from '@/common/api/tempoApi';
import type { DatetimeFilterKeys, ItemFiltersType } from '@/common/api/types/itemFilters';
import type {
  BatchItemAncestorsRequest,
  BulkFetchV2Response,
  BulkPartialPatchRequest,
  BulkPartialPatchResponse,
  CreateItemRequest,
  Item,
  ItemAncestor,
  ItemDataSet,
  ItemPatchRequest,
  ItemRequestModelField,
  PaginatedItems,
} from '@/common/api/types/items';
import { datetimeItemFilterKeys } from '@/common/components/filters/types';
import { UNASSIGNED_FILTER_VALUE } from '@/common/components/filters/utils/filter-options/filterOptionsUtils';
import { BULK_FETCH_MAX_ITEMS } from '@/common/constants';
import { chunkBy } from '@/common/utils/chunkBy';
import {
  and,
  containsIgnoreCase,
  equals,
  excludes,
  greaterThan,
  greaterThanOrEqualTo,
  includes,
  isNotNull,
  isNull,
  lessThan,
  lessThanOrEqualTo,
  notContainsIgnoreCase,
  notEquals,
  type Operator,
  or,
  startsWithIgnoreCase,
} from '@/common/utils/rsqlFilters';
import { fetchChunksWithConcurrencyLimit } from './chunkedFetch';

export const getOperationType = (key: string, value: string | string[], operator: Operator) => {
  switch (operator) {
    case 'eq':
      if (Array.isArray(value)) {
        return includes(key, value);
      }
      return equals(key, value);
    case 'containsIgnoreCase':
      if (Array.isArray(value)) {
        return null;
      }
      return containsIgnoreCase(key, value);
    case 'startsWithIgnoreCase':
      if (Array.isArray(value)) {
        return null;
      }
      return startsWithIgnoreCase(key, value);
    case 'in':
      if (!Array.isArray(value)) {
        return equals(key, value);
      }
      if (Array.isArray(value) && value.length === 1) {
        return equals(key, value[0] ?? '');
      }
      return includes(key, Array.isArray(value) ? value : [value]);
    case 'null':
      return isNull(key);
    case 'notnull':
      return isNotNull(key);
    case 'gt':
      if (Array.isArray(value)) {
        return null;
      }
      return greaterThan(key, value);
    case 'ge':
      if (Array.isArray(value)) {
        return null;
      }
      return greaterThanOrEqualTo(key, value);
    case 'lt':
      if (Array.isArray(value)) {
        return null;
      }
      return lessThan(key, value);
    case 'le':
      if (Array.isArray(value)) {
        return null;
      }
      return lessThanOrEqualTo(key, value);
    case 'ne':
      if (Array.isArray(value)) {
        return null;
      }
      return notEquals(key, value);
    default:
      if (Array.isArray(value)) {
        return null;
      }
      return equals(key, value);
  }
};

const isMultiDescendantFieldSearch = (filter: ItemFiltersType): boolean => {
  const hasDescendantName = filter['descendant.name']?.value;
  const hasDescendantExternalKey = filter['descendant.externalKey']?.value;

  return !!(
    hasDescendantName &&
    hasDescendantExternalKey &&
    filter['descendant.name']?.value === filter['descendant.externalKey']?.value &&
    filter['descendant.name']?.operator === 'containsIgnoreCase' &&
    filter['descendant.externalKey']?.operator === 'containsIgnoreCase'
  );
};

const isMultiFieldNameSearch = (filter: ItemFiltersType): boolean => {
  const hasName = filter.name?.value;
  const hasExternalKey = filter.externalKey?.value;

  return !!(
    hasName &&
    hasExternalKey &&
    filter.name?.value === filter.externalKey?.value &&
    filter.name?.operator === 'containsIgnoreCase' &&
    filter.externalKey?.operator === 'containsIgnoreCase'
  );
};

const handleNullFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (filterValue.operator === 'null') {
    return isNull(filterKey);
  }
  if (filterValue.operator === 'notnull') {
    return isNotNull(filterKey);
  }
  if (filterValue.value === null) {
    return isNull(filterKey);
  }
  return undefined;
};

const ESTIMATION_FILTER_KEYS = new Set(['estimationId', 'descendant.estimationId']);

const handleUnassignedEstimationFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (!ESTIMATION_FILTER_KEYS.has(filterKey) || !Array.isArray(filterValue.value)) {
    return undefined;
  }

  const values = filterValue.value.map(String);
  if (!values.includes(UNASSIGNED_FILTER_VALUE)) {
    return undefined;
  }

  const realIds = values.filter((v) => v !== UNASSIGNED_FILTER_VALUE);
  return realIds.length === 0
    ? isNull(filterKey)
    : or(includes(filterKey, realIds), isNull(filterKey));
};

const handleDatetimeFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (!datetimeItemFilterKeys.includes(filterKey as DatetimeFilterKeys)) {
    return undefined;
  }

  if (
    (filterValue.operator === 'in' || filterValue.operator === 'eq') &&
    Array.isArray(filterValue.value) &&
    filterValue.value.length === 2
  ) {
    return and(
      greaterThanOrEqualTo(filterKey, filterValue.value[0] as string),
      lessThanOrEqualTo(filterKey, filterValue.value[1] as string),
    );
  }

  if (
    filterValue.operator === 'ne' &&
    Array.isArray(filterValue.value) &&
    filterValue.value.length === 2
  ) {
    return or(
      lessThan(filterKey, filterValue.value[0] as string),
      greaterThan(filterKey, filterValue.value[1] as string),
    );
  }

  return undefined;
};

const handleStringFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (typeof filterValue.value !== 'string') {
    return undefined;
  }

  if (filterValue.operator === 'containsIgnoreCase') {
    return containsIgnoreCase(filterKey, filterValue.value);
  }
  if (filterValue.operator === 'startsWithIgnoreCase') {
    return startsWithIgnoreCase(filterKey, filterValue.value);
  }
  return equals(filterKey, filterValue.value);
};

const handleArrayFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (!Array.isArray(filterValue.value)) {
    return undefined;
  }

  if (filterValue.value.length === 0) {
    return undefined;
  }

  if (filterValue.value.length === 1 && filterValue.value[0]) {
    return (
      getOperationType(
        filterKey,
        filterValue.value[0].toString(),
        filterValue.operator as Operator,
      ) ?? undefined
    );
  }

  return (
    getOperationType(filterKey, filterValue.value.map(String), filterValue.operator as Operator) ??
    undefined
  );
};

const handleGenericFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (filterValue.value === undefined) {
    return undefined;
  }
  if (Array.isArray(filterValue.value) && filterValue.value.length === 0) {
    return undefined;
  }

  return (
    getOperationType(filterKey, String(filterValue.value), filterValue.operator as Operator) ??
    undefined
  );
};

const CUSTOM_FIELD_TYPE_MAP: Record<string, string> = {
  text: 'Text',
  date: 'Date',
  checked: 'Checked',
  picklist: 'PicklistChoiceId',
  userId: 'UserId',
  itemId: 'ItemId',
  link: 'Link',
  number: 'Number',
  currency: 'Currency',
  customerId: 'CustomerId',
};

const toDateOnly = (value: string): string => value.split('T')[0] ?? value;

const handleCustomFieldFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  const match = filterKey.match(/^cf\.(\d+)\.(\w+)$/);
  if (!match) return undefined;

  const fieldId = match[1];
  const fieldType = match[2];
  const rsqlProperty = CUSTOM_FIELD_TYPE_MAP[fieldType ?? ''];
  if (!rsqlProperty) return undefined;

  const rsqlKey = `field${fieldId}${rsqlProperty}`;

  if (fieldType === 'text') {
    if (filterValue.operator === 'null') return isNull(rsqlKey);
    if (filterValue.operator === 'notnull') return isNotNull(rsqlKey);

    const rawValue = Array.isArray(filterValue.value) ? filterValue.value[0] : filterValue.value;
    if (typeof rawValue === 'string' && rawValue.trim()) {
      const trimmed = rawValue.trim();
      if (filterValue.operator === 'eq') return equals(rsqlKey, trimmed);
      if (filterValue.operator === 'notContainsIgnoreCase') {
        return notContainsIgnoreCase(rsqlKey, trimmed);
      }
      return containsIgnoreCase(rsqlKey, trimmed);
    }
  }

  if (fieldType === 'link') {
    if (typeof filterValue.value === 'string' && filterValue.value.trim()) {
      return containsIgnoreCase(rsqlKey, filterValue.value.trim());
    }
    if (Array.isArray(filterValue.value) && filterValue.value.length > 0 && filterValue.value[0]) {
      return containsIgnoreCase(rsqlKey, String(filterValue.value[0]).trim());
    }
  }

  if (fieldType === 'number' || fieldType === 'currency') {
    if (filterValue.operator === 'null') return isNull(rsqlKey);
    if (filterValue.operator === 'notnull') return isNotNull(rsqlKey);

    if (
      filterValue.operator === 'in' &&
      Array.isArray(filterValue.value) &&
      filterValue.value.length === 2
    ) {
      return and(
        greaterThanOrEqualTo(rsqlKey, String(filterValue.value[0])),
        lessThanOrEqualTo(rsqlKey, String(filterValue.value[1])),
      );
    }

    if (typeof filterValue.value === 'number') {
      return (
        getOperationType(rsqlKey, String(filterValue.value), filterValue.operator as Operator) ??
        undefined
      );
    }

    if (
      Array.isArray(filterValue.value) &&
      filterValue.value.length > 0 &&
      filterValue.value[0] !== undefined
    ) {
      return (
        getOperationType(rsqlKey, String(filterValue.value[0]), filterValue.operator as Operator) ??
        undefined
      );
    }
  }

  if (fieldType === 'date') {
    if (filterValue.operator === 'null') return isNull(rsqlKey);
    if (filterValue.operator === 'notnull') return isNotNull(rsqlKey);

    if (
      (filterValue.operator === 'in' || filterValue.operator === 'eq') &&
      Array.isArray(filterValue.value) &&
      filterValue.value.length === 2
    ) {
      return and(
        greaterThanOrEqualTo(rsqlKey, toDateOnly(filterValue.value[0] as string)),
        lessThanOrEqualTo(rsqlKey, toDateOnly(filterValue.value[1] as string)),
      );
    }

    if (
      filterValue.operator === 'ne' &&
      Array.isArray(filterValue.value) &&
      filterValue.value.length === 2
    ) {
      return or(
        lessThan(rsqlKey, toDateOnly(filterValue.value[0] as string)),
        greaterThan(rsqlKey, toDateOnly(filterValue.value[1] as string)),
      );
    }

    if (typeof filterValue.value === 'string') {
      return (
        getOperationType(
          rsqlKey,
          toDateOnly(filterValue.value),
          filterValue.operator as Operator,
        ) ?? undefined
      );
    }

    if (
      Array.isArray(filterValue.value) &&
      filterValue.value.length === 1 &&
      filterValue.value[0]
    ) {
      return (
        getOperationType(
          rsqlKey,
          toDateOnly(filterValue.value[0] as string),
          filterValue.operator as Operator,
        ) ?? undefined
      );
    }
  }

  if (fieldType === 'checked') {
    const boolValue = Array.isArray(filterValue.value) ? filterValue.value[0] : filterValue.value;
    if (boolValue === 'true' || boolValue === 'false' || typeof boolValue === 'boolean') {
      return equals(rsqlKey, String(boolValue));
    }
  }

  if (fieldType === 'picklist' || fieldType === 'itemId' || fieldType === 'customerId') {
    if (fieldType === 'picklist' && filterValue.operator === 'notIn') {
      if (Array.isArray(filterValue.value) && filterValue.value.length > 0) {
        return excludes(rsqlKey, filterValue.value.map(String));
      }
      if (typeof filterValue.value === 'string' || typeof filterValue.value === 'number') {
        return excludes(rsqlKey, [String(filterValue.value)]);
      }
    }

    if (Array.isArray(filterValue.value) && filterValue.value.length > 0) {
      if (filterValue.value.length === 1) {
        return equals(rsqlKey, String(filterValue.value[0]));
      }
      return includes(rsqlKey, filterValue.value.map(String));
    }
    if (typeof filterValue.value === 'string' || typeof filterValue.value === 'number') {
      return equals(rsqlKey, String(filterValue.value));
    }
  }

  if (fieldType === 'userId') {
    if (Array.isArray(filterValue.value) && filterValue.value.length > 0) {
      if (filterValue.value.length === 1) {
        return equals(rsqlKey, String(filterValue.value[0]));
      }
      return includes(rsqlKey, filterValue.value.map(String));
    }
    if (typeof filterValue.value === 'string' && filterValue.value.trim()) {
      return equals(rsqlKey, filterValue.value.trim());
    }
  }

  return undefined;
};

const buildSingleFilter = (filterKey: string, filterValueRaw: unknown): string | undefined => {
  if (!filterValueRaw || typeof filterValueRaw !== 'object') {
    return undefined;
  }

  const filterValue = filterValueRaw as { operator?: string; value?: unknown };

  return (
    handleCustomFieldFilter(filterKey, filterValue) ||
    handleNullFilter(filterKey, filterValue) ||
    handleUnassignedEstimationFilter(filterKey, filterValue) ||
    handleDatetimeFilter(filterKey, filterValue) ||
    handleStringFilter(filterKey, filterValue) ||
    handleArrayFilter(filterKey, filterValue) ||
    handleGenericFilter(filterKey, filterValue)
  );
};

const buildMultiDescendantFieldSearchFilter = (filter: ItemFiltersType): string | undefined => {
  const searchValue = filter['descendant.name']?.value;
  if (!searchValue) return undefined;

  const searchFilter = or(
    containsIgnoreCase('descendant.name', searchValue),
    containsIgnoreCase('descendant.externalKey', searchValue),
  );

  const otherFilters = Object.entries(filter)
    .filter(
      ([filterKey]) => filterKey !== 'descendant.name' && filterKey !== 'descendant.externalKey',
    )
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  const allFilters = [searchFilter, ...otherFilters];
  return allFilters.length > 0 ? and(...allFilters) : undefined;
};

const buildMultiFieldNameSearchFilter = (filter: ItemFiltersType): string | undefined => {
  const searchValue = filter.name?.value;
  if (!searchValue) return undefined;

  const searchFilter = or(
    containsIgnoreCase('name', searchValue),
    containsIgnoreCase('externalKey', searchValue),
  );

  const otherFilters = Object.entries(filter)
    .filter(([filterKey]) => filterKey !== 'name' && filterKey !== 'externalKey')
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  const allFilters = [searchFilter, ...otherFilters];
  return allFilters.length > 0 ? and(...allFilters) : undefined;
};

const buildHealthFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { health, ...remaining } = filter as ItemFiltersType & {
    health?: { value?: string | string[] };
  };

  const values = Array.isArray(health?.value) ? health.value : health?.value ? [health.value] : [];
  if (values.length === 0) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: includes('healthLevel', values),
    remaining,
  };
};

const buildHasDependenciesFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { hasDependencies, ...remaining } = filter as ItemFiltersType & {
    hasDependencies?: { value?: boolean };
  };

  if (!hasDependencies?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: equals('hasDependencies', 'true'),
    remaining,
  };
};

const buildHasPredecessorFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { hasPredecessor, ...remaining } = filter as ItemFiltersType & {
    hasPredecessor?: { value?: boolean };
  };

  if (!hasPredecessor?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: equals('hasDependencies.predecessors', 'true'),
    remaining,
  };
};

const buildHasSuccessorFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { hasSuccessor, ...remaining } = filter as ItemFiltersType & {
    hasSuccessor?: { value?: boolean };
  };

  if (!hasSuccessor?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: equals('hasDependencies.successors', 'true'),
    remaining,
  };
};

const buildHasEstimateFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { hasEstimate, ...remaining } = filter as ItemFiltersType & {
    hasEstimate?: { value?: boolean };
  };

  if (!hasEstimate?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNotNull('estimationId'),
    remaining,
  };
};

const buildNoEstimateFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { noEstimate, ...remaining } = filter as ItemFiltersType & {
    noEstimate?: { value?: boolean };
  };

  if (!noEstimate?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNull('estimationId'),
    remaining,
  };
};

const buildHasProgressFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { hasProgress, ...remaining } = filter as ItemFiltersType & {
    hasProgress?: { value?: boolean };
  };

  if (!hasProgress?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNotNull('progressPercentage'),
    remaining,
  };
};

const buildNoProgressFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { noProgress, ...remaining } = filter as ItemFiltersType & {
    noProgress?: { value?: boolean };
  };

  if (!noProgress?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNull('progressPercentage'),
    remaining,
  };
};

// item-store has no isAtRisk field (only reporting-store does, via buildScenarioItemFiltersRsql
// in plannerDataSet.ts), so this strips the key instead of emitting RSQL item-store would reject.
const buildAtRiskFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { atRisk: _atRisk, ...remaining } = filter as ItemFiltersType & {
    atRisk?: { value?: boolean };
  };

  return { rsql: undefined, remaining };
};

const buildNeedsTeamOrEstimateFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { needsTeamOrEstimate, ...remaining } = filter as ItemFiltersType & {
    needsTeamOrEstimate?: { value?: boolean };
  };

  if (!needsTeamOrEstimate?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: or(equals('descendant.teamId', '0'), equals('descendant.estimationId', '0')),
    remaining,
  };
};

const buildUnresolvedFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { unresolved, ...remaining } = filter as ItemFiltersType & {
    unresolved?: { value?: boolean };
  };

  if (!unresolved?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: notEquals('statusItemStatusType', 'DONE'),
    remaining,
  };
};

const buildIsUnparentedFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { isUnparented, ...remaining } = filter as ItemFiltersType & {
    isUnparented?: { value?: boolean };
  };

  if (!isUnparented?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNull('parentId'),
    remaining,
  };
};

const buildHasObjectiveFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { hasObjective, ...remaining } = filter as ItemFiltersType & {
    hasObjective?: { value?: boolean };
  };

  if (!hasObjective?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: equals('hasObjective', 'false'),
    remaining,
  };
};

const buildImportedItemsFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { importedItems, ...remaining } = filter as ItemFiltersType & {
    importedItems?: { value?: boolean };
  };

  if (!importedItems?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNotNull('descendant.externalKey'),
    remaining,
  };
};

const buildLocallyCreatedItemsFilter = (
  filter: ItemFiltersType,
): { rsql: string | undefined; remaining: ItemFiltersType } => {
  const { locallyCreatedItems, ...remaining } = filter as ItemFiltersType & {
    locallyCreatedItems?: { value?: boolean };
  };

  if (!locallyCreatedItems?.value) {
    return { rsql: undefined, remaining };
  }

  return {
    rsql: isNull('descendant.externalKey'),
    remaining,
  };
};

export const buildFilterString = (filter?: ItemFiltersType): string | undefined => {
  if (!filter) return undefined;

  const { rsql: needsTeamOrEstimateRsql, remaining: afterQuickFilter } =
    buildNeedsTeamOrEstimateFilter(filter);
  const { rsql: unresolvedRsql, remaining: afterUnresolved } =
    buildUnresolvedFilter(afterQuickFilter);
  const { rsql: isUnparentedRsql, remaining: afterUnparented } =
    buildIsUnparentedFilter(afterUnresolved);
  const { rsql: importedItemsRsql, remaining: afterImported } =
    buildImportedItemsFilter(afterUnparented);
  const { rsql: locallyCreatedItemsRsql, remaining: afterLocal } =
    buildLocallyCreatedItemsFilter(afterImported);
  const { rsql: hasDependenciesRsql, remaining: afterDependencies } =
    buildHasDependenciesFilter(afterLocal);
  const { rsql: hasPredecessorRsql, remaining: afterPredecessor } =
    buildHasPredecessorFilter(afterDependencies);
  const { rsql: hasSuccessorRsql, remaining: afterSuccessor } =
    buildHasSuccessorFilter(afterPredecessor);
  const { rsql: hasEstimateRsql, remaining: afterHasEstimate } =
    buildHasEstimateFilter(afterSuccessor);
  const { rsql: noEstimateRsql, remaining: afterNoEstimate } =
    buildNoEstimateFilter(afterHasEstimate);
  const { rsql: hasObjectiveRsql, remaining: afterHasObjective } =
    buildHasObjectiveFilter(afterNoEstimate);
  const { rsql: hasProgressRsql, remaining: afterHasProgress } =
    buildHasProgressFilter(afterHasObjective);
  const { rsql: noProgressRsql, remaining: afterNoProgress } =
    buildNoProgressFilter(afterHasProgress);
  const { rsql: atRiskRsql, remaining: afterAtRisk } = buildAtRiskFilter(afterNoProgress);
  const { rsql: healthRsql, remaining } = buildHealthFilter(afterAtRisk);

  const extractedRsqls = [
    needsTeamOrEstimateRsql,
    unresolvedRsql,
    isUnparentedRsql,
    importedItemsRsql,
    locallyCreatedItemsRsql,
    hasDependenciesRsql,
    hasPredecessorRsql,
    hasSuccessorRsql,
    hasEstimateRsql,
    noEstimateRsql,
    hasObjectiveRsql,
    hasProgressRsql,
    noProgressRsql,
    atRiskRsql,
    healthRsql,
  ].filter(Boolean) as string[];

  if (isMultiDescendantFieldSearch(remaining)) {
    const multiFieldRsql = buildMultiDescendantFieldSearchFilter(remaining);
    if (!multiFieldRsql && extractedRsqls.length === 0) return undefined;
    const parts = [multiFieldRsql, ...extractedRsqls].filter(Boolean) as string[];
    return and(...parts);
  }

  if (isMultiFieldNameSearch(remaining)) {
    const multiFieldRsql = buildMultiFieldNameSearchFilter(remaining);
    if (!multiFieldRsql && extractedRsqls.length === 0) return undefined;
    const parts = [multiFieldRsql, ...extractedRsqls].filter(Boolean) as string[];
    return and(...parts);
  }

  const filters = Object.entries(remaining)
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  filters.push(...extractedRsqls);

  if (filters.length === 0) return undefined;

  return and(...filters);
};

const buildItemSearchQueryPrams = ({
  cursor,
  filter,
  limit,
  order,
  hierarchical,
}: {
  cursor?: string;
  filter?: ItemFiltersType;
  limit?: number;
  order?: string;
  hierarchical?: boolean;
}) => {
  const params = new URLSearchParams();

  if (cursor) {
    params.set('cursor', cursor);
  }

  if (order) {
    params.set('order', order);
  }

  if (filter) {
    const filterString = buildFilterString(filter);
    if (filterString) {
      params.set('filter', filterString);
    }
  }

  if (limit) {
    params.set('limit', String(limit));
  }

  if (hierarchical !== undefined) {
    params.set('hierarchical', String(hierarchical));
  }

  return params.toString() ? `?${params.toString()}` : '';
};

const getItems = async ({
  cursor = '',
  filter,
  limit,
  order = 'priority',
  hierarchical,
}: {
  cursor?: string;
  filter?: ItemFiltersType;
  limit?: number;
  order?: string;
  hierarchical?: boolean;
}): Promise<PaginatedItems> => {
  const url = `api/v1/item-store/items${buildItemSearchQueryPrams({ cursor, filter, limit, order, hierarchical })}`;
  return await tempoApi().get(url).json();
};

const getItemsDataSet = async ({
  order,
  filter,
}: {
  filter?: ItemFiltersType;
  order?: string;
}): Promise<ItemDataSet> => {
  const url = `api/v1/item-store/items/data-set${buildItemSearchQueryPrams({ filter, order })}`;
  return await tempoApi().get(url).json();
};

const getInsightsItemsDataSet = async ({
  order,
  filterString = '',
}: {
  filterString?: string;
  order?: string;
}): Promise<ItemDataSet> => {
  const params = new URLSearchParams();
  if (order) {
    params.set('order', order);
  }
  if (filterString) {
    params.set('filter', filterString);
  }
  const query = params.toString();
  const url = `api/v1/item-store/items/data-set${query ? `?${query}` : ''}`;

  return await tempoApi().get(url).json();
};

const getItemsBulkFetch = async ({
  ids,
  fields,
  customFieldIds,
  filter,
  signal,
}: {
  ids: number[];
  fields?: ItemRequestModelField[];
  customFieldIds?: number[];
  filter?: ItemFiltersType;
  signal?: AbortSignal;
}) => {
  const searchParams = new URLSearchParams();
  const filterString = buildFilterString(filter);
  if (filterString) {
    searchParams.set('filter', filterString);
  }

  return await tempoApi()
    .post('api/v1/item-store/items/bulk-fetch', {
      json: {
        ids,
        fields: fields ?? null,
        customFieldIds: customFieldIds ?? null,
      },
      searchParams,
      signal,
    })
    .json<Item[]>();
};

const getItemsBulkFetchV2 = async ({
  ids,
  fieldIds,
  customFieldIds,
  signal,
}: {
  ids: number[];
  fieldIds?: string[];
  customFieldIds?: number[];
  signal?: AbortSignal;
}) =>
  await tempoApi()
    .post('api/v1/item-store/items/bulk-fetch/v2', {
      json: {
        ids,
        fieldIds: fieldIds ?? null,
        customFieldIds: customFieldIds ?? null,
      },
      signal,
    })
    .json<BulkFetchV2Response>();

const getItemsBulkFetchChunked = async (params: {
  ids: number[];
  fields?: ItemRequestModelField[];
  customFieldIds?: number[];
  filter?: ItemFiltersType;
  signal?: AbortSignal;
}): Promise<Item[]> => {
  const { ids, ...rest } = params;
  if (ids.length === 0) {
    return [];
  }
  if (ids.length <= BULK_FETCH_MAX_ITEMS) {
    return getItemsBulkFetch({ ids, ...rest });
  }
  const responses = await fetchChunksWithConcurrencyLimit(
    chunkBy(ids, BULK_FETCH_MAX_ITEMS),
    (chunk) => getItemsBulkFetch({ ids: chunk, ...rest }),
  );
  return responses.flat();
};

const getItemsBulkFetchV2Chunked = async (params: {
  ids: number[];
  fieldIds?: string[];
  customFieldIds?: number[];
  signal?: AbortSignal;
}): Promise<BulkFetchV2Response> => {
  const { ids, ...rest } = params;
  if (ids.length === 0) {
    return { itemsById: {} };
  }
  if (ids.length <= BULK_FETCH_MAX_ITEMS) {
    return getItemsBulkFetchV2({ ids, ...rest });
  }
  const responses = await fetchChunksWithConcurrencyLimit(
    chunkBy(ids, BULK_FETCH_MAX_ITEMS),
    (chunk) => getItemsBulkFetchV2({ ids: chunk, ...rest }),
  );
  return { itemsById: Object.assign({}, ...responses.map((r) => r.itemsById)) };
};

const getItemById = async ({ itemId }: { itemId: number }) =>
  await tempoApi().get(`api/v1/item-store/items/${itemId}`).json<Item>();

const getItemAncestors = async ({ itemId }: { itemId: number }) =>
  await tempoApi().get(`api/v1/item-store/items/${itemId}/ancestors`).json<ItemAncestor[]>();

const getItemAncestorsBatch = async (request: BatchItemAncestorsRequest) =>
  await tempoApi()
    .post('api/v1/item-store/items/ancestors', { json: request })
    .json<Record<number, ItemAncestor[]>>();

const updateItem = async ({
  itemId,
  data,
  signal,
}: {
  itemId: number;
  data: ItemPatchRequest;
  signal?: AbortSignal;
}) =>
  await tempoApi().patch(`api/v1/item-store/items/${itemId}`, { json: data, signal }).json<Item>();

const bulkUpdateItems = async (items: Array<{ id: number } & ItemPatchRequest>) =>
  await tempoApi().patch('api/v1/item-store/items/bulk', { json: items }).json<Item[]>();

const createItem = async (data: CreateItemRequest) =>
  await tempoApi().post('api/v1/item-store/items', { json: data }).json<Item>();

const deleteItem = async ({ itemId }: { itemId: number }): Promise<void> => {
  await tempoApi().delete(`api/v1/item-store/items/${itemId}`);
};

const searchItemsWithCustomFields = async (): Promise<Item[]> => {
  const allItems: Item[] = [];
  let cursor: string | undefined;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = await getItems({
      limit: 100,
      hierarchical: false,
      cursor,
    });

    allItems.push(...result.data);

    if (!('nextCursor' in result) || result.nextCursor == null) {
      break;
    }

    cursor = result.nextCursor as string | undefined;
  }

  return allItems;
};

const bulkPartialPatchItems = (
  request: BulkPartialPatchRequest,
): Promise<BulkPartialPatchResponse> =>
  tempoApi()
    .patch('api/v1/item-store/items/bulk/v2', { json: request })
    .json<BulkPartialPatchResponse>();

export const ItemsApi = {
  getItems,
  getItemsDataSet,
  getInsightsItemsDataSet,
  getItemsBulkFetch,
  getItemsBulkFetchChunked,
  getItemsBulkFetchV2,
  getItemsBulkFetchV2Chunked,
  getItemById,
  getItemAncestors,
  getItemAncestorsBatch,
  updateItem,
  bulkUpdateItems,
  createItem,
  deleteItem,
  searchItemsWithCustomFields,
  bulkPartialPatchItems,
};
